import { useCallback, useEffect, useState } from 'react';
import { IconDownload, IconTrash, IconPhoto, IconLoader2, IconSearch, IconX, IconLayoutGrid, IconList } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import printTemplateService from '../../../services/printTemplateService';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import DatePickerField from '../../../components/common/DatePickerField';
import SelectField from '../../../components/common/SelectField';
import { useAuthStore } from '../../../store/authStore';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useDebouncedValue } from '@/lib/hooks';
import { useTranslation } from '../../../i18n/hooks/useTranslation';

function fmtDate(iso) {
    try {
        return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
        return '';
    }
}

// A picked date filters that WHOLE day (00:00 → 23:59.999); a start-only bound would
// return everything from that day onward (looks like "no filter").
const toDayRange = (date) => {
    if (!date) return [null, null];
    const start = new Date(date); start.setHours(0, 0, 0, 0);
    const end   = new Date(date); end.setHours(23, 59, 59, 999);
    return [start, end];
};

/** Print media the user has exported — listed from GeneratedPrintMedia. */
export default function PrintMediaTab({ officeSpaceId, onOpenEditor }) {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(null);
    const [zipping, setZipping] = useState(null);

    // Filters — search by title + single-day date range (matches the other tabs).
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 300);
    const [dateRange, setDateRange] = useState([null, null]);
    const [sortOrder, setSortOrder] = useState('newest');
    const hasFilter = !!debouncedSearch || !!dateRange[0];
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('printMediaViewMode') || 'grid');
    const setView = (mode) => { setViewMode(mode); localStorage.setItem('printMediaViewMode', mode); };

    // The backend requires an officeSpaceId for non-admin users. For OWNER/OPERATOR
    // it's resolved asynchronously by the office-space store, so on first render it's
    // still null — firing the request now would 400 with "Office space ID is required".
    // Wait until the scope resolves (or the store finishes loading) before querying.
    const { user } = useAuthStore();
    const { isLoading: officeSpacesLoading } = useOfficeSpaceStore();
    const isAdminRole = user?.role === 'admin' || user?.role === 'superadmin';
    const canQuery = isAdminRole || !!officeSpaceId;

    // Server zips PNG+PDF and sends it as an attachment. Going through the api client
    // (rather than a plain link) keeps the auth header and forces a real download —
    // a cross-origin R2 link would just open in a tab.
    const downloadZip = async (it) => {
        setZipping(it.id);
        try {
            const blob = await printTemplateService.downloadGeneratedZip(
                it.id,
                officeSpaceId ? { officeSpaceId } : {},
            );
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${it.title || 'print-media'}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err.message || t('printMedia.tabDownloadFailed'),
                color: 'red',
            });
        } finally {
            setZipping(null);
        }
    };

    const load = useCallback(async () => {
        if (!canQuery) return; // office-space scope not resolved yet — skip the doomed request
        setLoading(true);
        try {
            const res = await printTemplateService.listGenerated({
                ...(officeSpaceId ? { officeSpaceId } : {}),
                ...(debouncedSearch ? { search: debouncedSearch } : {}),
                ...(dateRange[0] ? { startDate: dateRange[0].toISOString() } : {}),
                ...(dateRange[1] ? { endDate: dateRange[1].toISOString() } : {}),
                ...(sortOrder ? { sortOrder } : {}),
                limit: 60,
            });
            setItems(res?.data?.items || []);
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err.message || t('printMedia.tabLoadFailed'),
                color: 'red',
            });
        } finally {
            setLoading(false);
        }
    }, [officeSpaceId, canQuery, debouncedSearch, dateRange, sortOrder]);

    useEffect(() => { load(); }, [load]);

    // If we can't query and the office-space store has finished loading (i.e. the user
    // genuinely has no office space), stop spinning and fall through to the empty state.
    useEffect(() => {
        if (!canQuery && !officeSpacesLoading) setLoading(false);
    }, [canQuery, officeSpacesLoading]);

    const confirmDelete = async () => {
        const row = deleting;
        if (!row) return;
        try {
            await printTemplateService.deleteGenerated(row.id, officeSpaceId ? { officeSpaceId } : {});
            setItems((prev) => prev.filter((i) => i.id !== row.id));
            notifications.show({ title: t('printMedia.tabDeleted'), message: t('printMedia.tabDeletedMsg'), color: 'green' });
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err.message || t('printMedia.tabDeleteFailed'), color: 'red' });
        } finally {
            setDeleting(null);
        }
    };

    // Genuinely empty (no print media yet AND no active filter) → onboarding empty state.
    // With a filter applied we keep the filter bar visible and show a "no results" note.
    if (!loading && !items.length && !hasFilter) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-6 py-16">
                <div className="max-w-md text-center">
                    <IconPhoto size={28} className="mx-auto text-zinc-400" />
                    <h2 className="mt-3 text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                        {t('printMedia.tabEmptyTitle')}
                    </h2>
                    <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                        {t('printMedia.tabEmptyDesc')}
                    </p>
                    <button
                        type="button"
                        onClick={onOpenEditor}
                        className="mt-4 px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm font-semibold"
                    >
                        {t('printMedia.tabOpenEditor')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Search + date filter (matches the images/documents tabs) */}
            <div className="flex items-center gap-3 mb-5 flex-wrap">
                <div className="flex-1 min-w-[200px] flex items-center gap-2.5 h-11 px-3.5 border border-zinc-200 dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900">
                    <IconSearch size={18} className="text-zinc-400 flex-shrink-0" />
                    <input
                        type="text"
                        className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400"
                        placeholder={t('printMedia.tabSearchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch('')}
                            aria-label={t('common.clear') || 'Clear'}
                            className="flex-shrink-0 p-0.5 rounded-full text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                        >
                            <IconX size={16} />
                        </button>
                    )}
                </div>
                <div className="w-full sm:w-[180px]">
                    <DatePickerField
                        value={dateRange[0]}
                        onChange={(date) => setDateRange(toDayRange(date))}
                        clearable
                        disabled={{ after: new Date() }}
                        triggerClassName="h-11 px-3.5 border border-zinc-200 dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-zinc-800 dark:text-zinc-100"
                    />
                </div>
                <div className="w-full sm:w-[160px]">
                    <SelectField
                        value={sortOrder}
                        onChange={setSortOrder}
                        options={[{ value: 'newest', label: t('common.newest') || 'Newest first' }, { value: 'oldest', label: t('common.oldest') || 'Oldest first' }]}
                        triggerClassName="h-11 px-3.5 border border-zinc-200 dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-zinc-800 dark:text-zinc-100"
                    />
                </div>
                <div className="flex gap-1 bg-[#F1EFEA] dark:bg-zinc-800 rounded-[10px] p-1 flex-shrink-0">
                    <button
                        type="button"
                        onClick={() => setView('list')}
                        className={`w-9 h-9 rounded-[7px] flex items-center justify-center transition-colors ${viewMode === 'list' ? 'bg-[#E2702E] text-white' : 'text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100'}`}
                    >
                        <IconList size={17} />
                    </button>
                    <button
                        type="button"
                        onClick={() => setView('grid')}
                        className={`w-9 h-9 rounded-[7px] flex items-center justify-center transition-colors ${viewMode === 'grid' ? 'bg-[#E2702E] text-white' : 'text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100'}`}
                    >
                        <IconLayoutGrid size={17} />
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex min-h-[30vh] items-center justify-center text-zinc-500">
                    <IconLoader2 size={18} className="animate-spin mr-2" /> {t('printMedia.tabLoading')}
                </div>
            ) : !items.length ? (
                <div className="flex min-h-[30vh] items-center justify-center rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    {t('printMedia.tabNoResults')}
                </div>
            ) : viewMode === 'grid' ? (
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                {items.map((it) => (
                    <div
                        key={it.id}
                        className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden flex flex-col"
                    >
                        <div className="aspect-[7/5] bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center overflow-hidden">
                            {it.thumbnailUrl || it.pngUrl ? (
                                <img
                                    src={it.thumbnailUrl || it.pngUrl}
                                    alt={it.title}
                                    className="w-full h-full object-contain"
                                    loading="lazy"
                                />
                            ) : (
                                <IconPhoto size={24} className="text-zinc-400" />
                            )}
                        </div>
                        <div className="p-3 flex-1 flex flex-col">
                            <p className="text-sm font-medium truncate text-zinc-900 dark:text-zinc-100" title={it.title}>
                                {it.title}
                            </p>
                            <p className="mt-0.5 text-xs text-zinc-500">
                                {fmtDate(it.createdAt)}
                                {it.width && it.height ? ` · ${it.width}×${it.height}` : ''}
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                                <button
                                    type="button"
                                    disabled={zipping === it.id}
                                    onClick={() => downloadZip(it)}
                                    className="flex-1 h-8 flex items-center justify-center gap-1.5 rounded-lg bg-zinc-900 text-white text-xs font-semibold disabled:opacity-50"
                                >
                                    {zipping === it.id
                                        ? <IconLoader2 size={13} className="animate-spin" />
                                        : <IconDownload size={13} />}
                                    {zipping === it.id ? t('printMedia.tabPreparing') : t('printMedia.tabPngPdf')}
                                </button>
                                <button
                                    type="button"
                                    title={t('printMedia.tabDelete')}
                                    onClick={() => setDeleting(it)}
                                    className="h-8 w-8 shrink-0 flex items-center justify-center rounded-lg border border-zinc-300 dark:border-zinc-600 text-red-600"
                                >
                                    <IconTrash size={13} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            ) : (
            <div className="flex flex-col gap-2">
                {items.map((it) => (
                    <div
                        key={it.id}
                        className="flex items-center gap-3 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-2.5"
                    >
                        <div className="w-16 h-12 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {it.thumbnailUrl || it.pngUrl ? (
                                <img
                                    src={it.thumbnailUrl || it.pngUrl}
                                    alt={it.title}
                                    className="w-full h-full object-contain"
                                    loading="lazy"
                                />
                            ) : (
                                <IconPhoto size={20} className="text-zinc-400" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate text-zinc-900 dark:text-zinc-100" title={it.title}>
                                {it.title}
                            </p>
                            <p className="mt-0.5 text-xs text-zinc-500">
                                {fmtDate(it.createdAt)}
                                {it.width && it.height ? ` · ${it.width}×${it.height}` : ''}
                            </p>
                        </div>
                        <button
                            type="button"
                            disabled={zipping === it.id}
                            onClick={() => downloadZip(it)}
                            className="h-8 px-3 flex items-center justify-center gap-1.5 rounded-lg bg-zinc-900 text-white text-xs font-semibold disabled:opacity-50 flex-shrink-0"
                        >
                            {zipping === it.id
                                ? <IconLoader2 size={13} className="animate-spin" />
                                : <IconDownload size={13} />}
                            <span className="hidden sm:inline">
                                {zipping === it.id ? t('printMedia.tabPreparing') : t('printMedia.tabPngPdf')}
                            </span>
                        </button>
                        <button
                            type="button"
                            title={t('printMedia.tabDelete')}
                            onClick={() => setDeleting(it)}
                            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-lg border border-zinc-300 dark:border-zinc-600 text-red-600"
                        >
                            <IconTrash size={13} />
                        </button>
                    </div>
                ))}
            </div>
            )}

            <ConfirmDialog
                open={!!deleting}
                onClose={() => setDeleting(null)}
                onConfirm={confirmDelete}
                title={t('printMedia.tabDeleteTitle')}
                description={t('printMedia.tabDeleteDesc', { title: deleting?.title || '' })}
                confirmLabel={t('printMedia.tabDelete')}
            />
        </>
    );
}
